import { RefObject } from 'react'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import DisposableReactionsStore from 'reusable/mobx-store-utils/DisposableReactionsStore'
import DateUtils from '@gap/utils/date-utils'
import { AvatarColorKey, colorKeyForUserId } from './avatarColor'
import { MentionUser, MentionSelection } from './MentionPopover'
import { makeAutoObservable } from 'mobx'

export interface DateOption {
   kind: 'date'
   subtype: 'today' | 'tomorrow' | 'pick'
   label: string
   iso?: string
}

export interface UserOption {
   kind: 'user'
   user: MentionUser
   group: 'team' | 'client'
}

export type MentionItem =
   | { section: string }
   | DateOption
   | UserOption

export type MentionSelectableRow =
   | { kind: 'date'; key: string; option: DateOption; selectableIndex: number }
   | { kind: 'user'; key: string; option: UserOption; selectableIndex: number; displayName: string; colorKey: AvatarColorKey }

export type MentionRenderRow =
   | { kind: 'section'; key: string; label: string; showDivider: boolean }
   | MentionSelectableRow

interface MentionPopoverStoreProps {
   query: string
   team: MentionUser[]
   clientUsers: MentionUser[]
   clientOrgName?: string | undefined
   hideDates?: boolean | undefined
   onSelect: (selection: MentionSelection) => void
   onClose: () => void
   scrollContainerRef: RefObject<HTMLDivElement>
}

function userName(user: MentionUser): string {
   if (user.name) return user.name
   const firstName = user.firstName ?? ''
   const lastName = user.lastName ?? ''
   return [firstName, lastName].filter(Boolean).join(' ').trim() || '(unknown)'
}

function isUserActive(user: MentionUser): boolean {
   return !user.deactivatedOn
}

export default class MentionPopoverStore {
   sp
   activeSelectableIndex = 0

   constructor(sp: MentionPopoverStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get items(): MentionItem[] {
      const lowercaseQuery = this.sp.query.trim().toLowerCase()
      const list: MentionItem[] = []

      if (!this.sp.hideDates) {
         const dateOptions: DateOption[] = [
            { kind: 'date', subtype: 'today', label: 'Today', iso: DateUtils.todayIso(0) },
            { kind: 'date', subtype: 'tomorrow', label: 'Tomorrow', iso: DateUtils.todayIso(1) },
            { kind: 'date', subtype: 'pick', label: 'Pick a date…' },
         ]
         const filteredDates = dateOptions.filter(option => !lowercaseQuery || option.label.toLowerCase().includes(lowercaseQuery))
         if (filteredDates.length) list.push({ section: 'Dates' }, ...filteredDates)
      }

      // Picker hides deactivated users entirely — but `:GapAssignee` chips already in
      // the document keep rendering them by snapshotted name (see GapAssigneePlugin).
      const teamOptions = this.sp.team
         .filter(isUserActive)
         .filter(user => !lowercaseQuery || userName(user).toLowerCase().includes(lowercaseQuery))
         .sort((a, b) => userName(a).localeCompare(userName(b)))
         .map<UserOption>(user => ({ kind: 'user', user, group: 'team' }))
      if (teamOptions.length) list.push({ section: 'Team' }, ...teamOptions)

      if (this.sp.clientUsers.length) {
         const clientOptions = this.sp.clientUsers
            .filter(isUserActive)
            .filter(user => !lowercaseQuery || userName(user).toLowerCase().includes(lowercaseQuery))
            .sort((a, b) => userName(a).localeCompare(userName(b)))
            .map<UserOption>(user => ({ kind: 'user', user, group: 'client' }))
         if (clientOptions.length) list.push({ section: this.sp.clientOrgName || 'Client' }, ...clientOptions)
      }

      return list
   }

   /** Indices into `items` of selectable rows (i.e. excluding section headers). */
   get selectableIndices(): number[] {
      return this.items.flatMap((item, index) => ('section' in item ? [] : [index]))
   }

   get isEmpty(): boolean {
      return this.selectableIndices.length === 0
   }

   /** Flattened render rows: section headers carry a `showDivider` flag so the view
    *  doesn't have to track group counts mid-map; selectable rows carry their
    *  pre-resolved `selectableIndex`. */
   get rows(): MentionRenderRow[] {
      const selectableIndices = this.selectableIndices
      let sectionCount = 0
      return this.items.map((item, itemIndex): MentionRenderRow => {
         if ('section' in item) {
            sectionCount += 1
            return { kind: 'section', key: `section-${itemIndex}`, label: item.section, showDivider: sectionCount > 1 }
         }
         const selectableIndex = selectableIndices.indexOf(itemIndex)
         if (item.kind === 'date') {
            return { kind: 'date', key: `date-${itemIndex}`, option: item, selectableIndex }
         }
         return {
            kind: 'user',
            key: `user-${item.user.id}-${itemIndex}`,
            option: item,
            selectableIndex,
            displayName: userName(item.user),
            colorKey: colorKeyForUserId(item.user.id),
         }
      })
   }

   /** Reset the highlight when the filter changes, scroll active row into view, and
    *  attach the global keyboard listener. All disposed when the host unmounts. */
   configureReactions = (reactionsStore: DisposableReactionsStore) => {
      reactionsStore.registerReaction({
         observedExpression: () => `${this.sp.query}|${this.sp.hideDates}`,
         reaction: () => { this.activeSelectableIndex = 0 },
      })
      reactionsStore.registerReaction({
         observedExpression: () => this.selectableIndices.length,
         reaction: count => {
            if (this.activeSelectableIndex >= count) this.activeSelectableIndex = Math.max(0, count - 1)
         },
      })
      reactionsStore.registerReaction({
         observedExpression: () => this.activeSelectableIndex,
         reaction: () => {
            // Defer scroll until after React paints the new selection so the DOM query hits.
            requestAnimationFrame(() => {
               const activeRowElement = this.sp.scrollContainerRef.current?.querySelector<HTMLElement>('[data-active="true"]')
               activeRowElement?.scrollIntoView({ block: 'nearest' })
            })
         },
      })
      window.addEventListener('keydown', this.handleKeyDown, true)
      reactionsStore.registerDisposer(() => window.removeEventListener('keydown', this.handleKeyDown, true))
   }

   setActiveSelectableIndex = (index: number) => {
      this.activeSelectableIndex = index
   }

   handleSelect = (row: MentionSelectableRow) => {
      this.commit(row.option)
   }

   private commit = (item: MentionItem) => {
      if ('section' in item) return
      if (item.kind === 'date') {
         if (item.subtype === 'pick') this.sp.onSelect({ kind: 'date-pick' })
         else if (item.iso) this.sp.onSelect({ kind: 'date', iso: item.iso })
      } else {
         const colorKey: AvatarColorKey = colorKeyForUserId(item.user.id)
         this.sp.onSelect({
            kind: 'user',
            userId: item.user.id,
            name: userName(item.user),
            colorKey,
            deactivated: !isUserActive(item.user),
         })
      }
   }

   private handleKeyDown = (event: KeyboardEvent) => {
      const indices = this.selectableIndices
      if (!indices.length) {
         if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
            this.sp.onClose()
         }
         return
      }
      if (event.key === 'ArrowDown') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.activeSelectableIndex = (this.activeSelectableIndex + 1) % indices.length
      } else if (event.key === 'ArrowUp') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.activeSelectableIndex = (this.activeSelectableIndex - 1 + indices.length) % indices.length
      } else if (event.key === 'Enter' || event.key === 'Tab') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.commit(this.items[indices[this.activeSelectableIndex]])
      } else if (event.key === 'Escape') {
         event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
         this.sp.onClose()
      }
   }
}

export const { Provider: MentionPopoverStoreProvider, useProvidedStore: useMentionPopoverStore } = createStoreContext(MentionPopoverStore)
